import { memo, type ReactNode } from 'react'
import type { MapIslandDto } from '@/api/hooks'
import { CaptainBlason } from '@/components/game/cosmetics/CaptainBlason'
import {
  GoldIcon, WoodIcon, RumIcon,
  PortIcon, SkullIcon, MysteryIcon, TaleIcon, ReefIcon, MineIcon, IslandIcon,
} from '@/components/game/GameIcons'
import { FACTION_LABELS, FACTION_TINT } from '@/lib/quests'
import { ReputationIcon } from '@/components/game/ReputationIcon'

// Récit (expedition) routes — matches the mystery-island purple they sail toward.
export const RECIT_COLOR = '#C4B5FD'

export const MISSION_COLOR: Record<string, string> = {
  commerce: '#E5CC8F',      // sand-300
  exploration: '#C4B5FD',   // purple-300
  scouting: '#A78BFA',      // violet-400 — recon theme; distinct from patrol (cyan) and exploration (purple-300)
  raid_pnj: '#FB923C',      // amber-400 — frees red for the incoming crimson line
  raid_pvp: '#EF4444',      // red-500 — my raid on another player; brighter than the incoming crimson (#B0143C)
  patrol: '#5FBDD0',        // ocean-300
  kraken_hunt: '#d9584a',   // rouge brique de la bête — distinct du raid PvP (#EF4444)
  quarry_convoy: '#8FD1DF', // ocean-200 : un convoi de charge, pas une sortie de guerre
}

export type MarkerMeta = { Icon: (p: { className?: string }) => ReactNode; color: string; label: string }

/** Island type → gold-line SVG icon, accent colour and legend label. */
export const ISLAND_META: Record<string, MarkerMeta> = {
  marine_port:      { Icon: PortIcon,    color: '#5FBDD0', label: 'Port Marine' },
  merchant_outpost: { Icon: GoldIcon,    color: '#E5CC8F', label: 'Comptoir' },
  production:       { Icon: MineIcon,    color: '#C9A55C', label: 'Production' },
  pirate_lair:      { Icon: SkullIcon,   color: '#F87171', label: 'Pirate' },
  mysterious:       { Icon: TaleIcon,    color: '#C4B5FD', label: 'Mystère' },
  recif:            { Icon: ReefIcon,    color: '#5FBDD0', label: 'Récif' },
  player_starter:   { Icon: IslandIcon,  color: '#5FBDD0', label: 'Joueur' },
  unknown:          { Icon: MysteryIcon, color: '#6b8a93', label: 'Inconnue' },
}

/**
 * Bucket de légende d'une île — la SEULE source de vérité partagée par la légende
 * cliquable (`MapLegend`) et les marqueurs (`MapScene`), pour qu'ils ne divergent
 * jamais. Miroir de `islandMarkerMeta` côté CLÉ : non découverte → 'unknown' ;
 * production spécialisée → 'production_wood' / 'production_rhum'. Une production
 * générique (sans spécialité) retombe sur 'production', qui n'a PAS de bouton (la
 * légende l'omet déjà) : elle s'atténue alors comme toute île non sélectionnée.
 */
export function islandLegendKey(island: MapIslandDto): string {
  if (!island.isDiscovered) return 'unknown'
  if (island.type === 'production') {
    if (island.specialty === 'wood') return 'production_wood'
    if (island.specialty === 'rhum') return 'production_rhum'
    // NOTE: 'production' (générique sans spécialité) n'est PAS dans LEGEND_ENTRIES —
    // aucune île ne peut le sélectionner, elle s'atténue donc dès qu'un filtre est actif.
    return 'production'
  }
  return island.type in ISLAND_META ? island.type : 'mysterious'
}

/**
 * Buckets de légende canoniques (clé → meta), dans l'ordre d'affichage. Production
 * est éclatée en ses deux spécialités pour coller à ce que la carte dessine ;
 * `islandLegendKey` renvoie ces mêmes clés. Réutilisé par `MapLegend` (boutons).
 */
export const LEGEND_ENTRIES: [string, MarkerMeta][] = Object.entries(ISLAND_META).flatMap(
  ([type, meta]) =>
    type === 'production'
      ? ([
          ['production_wood', { ...meta, Icon: WoodIcon, label: 'Scierie' }],
          ['production_rhum', { ...meta, Icon: RumIcon, label: 'Distillerie' }],
        ] as [string, MarkerMeta][])
      : ([[type, meta]] as [string, MarkerMeta][]),
)

/**
 * Per-island marker meta. Undiscovered islands always read as "unknown". A
 * discovered Production island swaps the generic mine icon for the one matching
 * its specialty — a scierie (wood) shows the wood marker, a distillerie (rhum)
 * the rhum marker — so the map tells producers apart at a glance. Everything
 * else keeps its type's marker. NB: `specialty` is the production resource, NOT
 * `threat.speciality` (combat style).
 */
export function islandMarkerMeta(island: MapIslandDto): MarkerMeta {
  if (!island.isDiscovered) return ISLAND_META.unknown
  if (island.type === 'production') {
    if (island.specialty === 'wood') return { ...ISLAND_META.production, Icon: WoodIcon, label: 'Scierie' }
    if (island.specialty === 'rhum') return { ...ISLAND_META.production, Icon: RumIcon, label: 'Distillerie' }
  }
  return ISLAND_META[island.type] ?? ISLAND_META.mysterious
}

/** Detail label for a discovered island: a specialized production island reads as its trade. */
export function islandTypeLabel(island: MapIslandDto): string {
  if (island.type === 'production' && island.specialty === 'wood') return 'Scierie'
  if (island.type === 'production' && island.specialty === 'rhum') return 'Distillerie'
  return island.typeLabel
}

type FactionKey = keyof typeof FACTION_LABELS

/** Narrow the DTO's free-form ownerFaction string to a known faction key (defaults neutral). */
function factionKey(raw: string | null): FactionKey {
  return raw !== null && raw in FACTION_LABELS ? (raw as FactionKey) : 'neutral'
}

/**
 * Owner pennant for a PLAYER island — pavillon emblem + faction label + notoriety
 * rank, tinted by faction (mirrors the quest-card chip). Reused wherever a foreign
 * captain's allegiance needs to read at a glance.
 */
export function FactionBadge({ faction, rank }: { faction: string | null; rank: number | null }) {
  const key = factionKey(faction)
  return (
    <span
      className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-xs font-semibold ${FACTION_TINT[key]}`}
      title={`${FACTION_LABELS[key]}${rank !== null ? ` · rang ${rank}` : ''}`}
    >
      <ReputationIcon faction={key} rank={rank ?? 0} size={16} className="shrink-0" />
      {FACTION_LABELS[key]}
      {rank !== null && <span className="text-ocean-300 tabular-nums">· rang {rank}</span>}
    </span>
  )
}

/**
 * Faction pennant for a PLAYER island MARKER — the owner's pavillon glyph in a
 * faction-tinted chip pinned to the marker's corner, the player-island analogue
 * of the NPC danger badge (which it never collides with: NPC islands carry the
 * danger badge, player islands this one). An owner who forged a blason in the
 * cabine flies IT instead of the faction pavillon (spec Lot 2) — the faction
 * stays readable via the tooltip and the detail panel's FactionBadge. Renders
 * nothing for NPC islands (no owner faction). Exported for isolated tests.
 *
 * Mémoïsé : MapScene re-rend TOUS les marqueurs à chaque frame de pan/zoom (le
 * contre-scale `k` change) ; l'objet `island` venant du cache TanStack Query est
 * référentiellement stable entre frames, donc memo évite de repeindre ~100
 * blasons SVG (~15 nœuds chacun) par frame de pinch.
 */
export const IslandFactionBadge = memo(function IslandFactionBadge({ island }: { island: MapIslandDto }) {
  if (null === island.ownerFaction) return null
  const key = factionKey(island.ownerFaction)
  const title = `${FACTION_LABELS[key]}${island.ownerRank !== null ? ` · rang ${island.ownerRank}` : ''}`
  if (island.ownerAppearance?.blason) {
    return (
      // Le blason perso est volontairement bien plus grand que le chip pavillon
      // (28 vs 16) : c'est la signature du joueur sur la carte, elle doit se lire
      // d'un coup d'œil (retours utilisateur T18).
      <span className="absolute -top-3 -left-3 w-8 h-8 grid place-items-center" title={title}>
        <CaptainBlason config={island.ownerAppearance.blason} size={28} variant="marker" />
      </span>
    )
  }
  return (
    <span
      className={`absolute -top-1.5 -left-1.5 rounded-full w-5 h-5 grid place-items-center border bg-ocean-900/90 ${FACTION_TINT[key]}`}
      title={title}
    >
      <ReputationIcon faction={key} rank={island.ownerRank ?? 0} size={18} />
    </span>
  )
})
